/**
 * Formatea una fecha (datetime nativa de `toma_*.fecha`, string ISO o Date) a
 * `DD/MM/YYYY` (es-AR). Devuelve '' si no hay fecha o es inválida — NUNCA inventa
 * una fecha (consistente con getTomasFechas del backend).
 *
 * Extraído desde `exportToExcel.ts` (issue #14) para reusar el MISMO formato en la
 * columna Fecha de la tabla visible (DataTable) sin duplicar lógica. El
 * comportamiento es idéntico al helper original del export.
 */
export const formatFecha = (raw: any): string => {
  if (raw === null || raw === undefined || raw === '') return '';

  let date: Date;
  if (raw instanceof Date) {
    date = raw;
  } else {
    const str = String(raw).trim();
    if (!str) return '';
    // Formato "YYYY-MM-DD" o "YYYY-MM-DD HH:mm:ss" → parsear la parte de fecha
    // en UTC-mediodía para evitar corrimiento de día por timezone.
    const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const [, y, mo, d] = m;
      date = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d), 12, 0, 0));
    } else {
      date = new Date(str);
    }
  }

  if (isNaN(date.getTime())) return '';

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getUTCDate())}/${pad(date.getUTCMonth() + 1)}/${date.getUTCFullYear()}`;
};

/**
 * Parsea un valor de fecha (datetime de `toma_*.fecha`, string ISO o Date) a un
 * `Date` en UTC-mediodía, con el MISMO criterio que `formatFecha` (evita
 * corrimiento de día por timezone). Devuelve `null` si no hay fecha o es inválida.
 * Helper interno reusado por formatFecha-family y getSemanaIso.
 */
const parseFechaUtc = (raw: any): Date | null => {
  if (raw === null || raw === undefined || raw === '') return null;
  let date: Date;
  if (raw instanceof Date) {
    date = raw;
  } else {
    const str = String(raw).trim();
    if (!str) return null;
    const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const [, y, mo, d] = m;
      date = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d), 12, 0, 0));
    } else {
      date = new Date(str);
    }
  }
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Devuelve el número de semana ISO 8601 en formato `AAAA-Www` (ej. `2026-W34`).
 *
 * ISO 8601: la semana empieza el lunes; la semana 1 es la que contiene el primer
 * jueves del año (equivalente: la que contiene el 4 de enero). El AÑO del prefijo
 * es el **año ISO de la semana**, que puede diferir del año calendario en los
 * bordes (ej. 31/12/2026 puede caer en `2027-W01`; 01/01/2027 en `2026-W53`).
 *
 * Reusa el mismo parseo UTC-mediodía que `formatFecha`. Sin fecha o inválida → ''
 * (nunca inventa — consistente con formatFecha).
 */
export const getSemanaIso = (raw: any): string => {
  const parsed = parseFechaUtc(raw);
  if (!parsed) return '';

  // Trabajar sobre una copia UTC para el cálculo ISO.
  const d = new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate()));
  // ISO: día de la semana con lunes=1 … domingo=7.
  const dayNum = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
  // Correr al jueves de la misma semana ISO → su año calendario es el año ISO.
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const isoYear = d.getUTCFullYear();
  // Primer día del año ISO y semanas transcurridas hasta el jueves.
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${isoYear}-W${pad(week)}`;
};
